import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, DatePicker, Input, Space, Spin, Switch, Table, Tag } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useMemo } from 'react';
import type { NotificationCategorySetting, NotificationClientHistory, NotificationInvoiceItem } from '../../DTOS/FacturiScadente';

interface NotificationTabProps {
  dateRange: [string | null, string | null];
  onDateRangeChange: (range: [string | null, string | null]) => void;
  clientName: string;
  onClientNameChange: (value: string) => void;
  cui: string;
  onCuiChange: (value: string) => void;
  onRefresh: () => void;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  historyData: NotificationClientHistory[];
  historyTotalItems: number;
  historyPage: number;
  historyPageSize: number;
  onHistoryPageChange: (page: number, pageSize: number) => void;
  categorySettings: NotificationCategorySetting[];
  isUpdatingCategory: boolean;
  onCategoryToggle: (category: string, isEnabled: boolean) => void;
  toNumber: (value: unknown) => number;
}

const NotificationTab: React.FC<NotificationTabProps> = ({
  dateRange,
  onDateRangeChange,
  clientName,
  onClientNameChange,
  cui,
  onCuiChange,
  onRefresh,
  isLoading,
  isError,
  errorMessage,
  historyData,
  historyTotalItems,
  historyPage,
  historyPageSize,
  onHistoryPageChange,
  categorySettings,
  isUpdatingCategory,
  onCategoryToggle,
  toNumber,
}) => {
  const rangePickerValue = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    const [start, end] = dateRange;
    if (!start && !end) {
      return null;
    }

    const startValue = start ? dayjs(start) : null;
    const endValue = end ? dayjs(end) : null;
    return [startValue, endValue];
  }, [dateRange]);

  const historyColumns = useMemo(
    () => [
      {
        title: 'Client',
        dataIndex: 'clientName',
        key: 'clientName',
        render: (value: string, record: NotificationClientHistory) => value || record.customerCui || '-',
      },
      {
        title: 'CUI',
        dataIndex: 'customerCui',
        key: 'customerCui',
      },
      {
        title: 'Nr. facturi',
        dataIndex: 'invoiceCount',
        key: 'invoiceCount',
      },
      {
        title: 'Total restant',
        dataIndex: 'totalOutstanding',
        key: 'totalOutstanding',
        render: (value: number) => (value != null ? toNumber(value).toLocaleString('ro-RO') : '-'),
      },
      {
        title: 'Moneda',
        dataIndex: 'currency',
        key: 'currency',
        render: (value: string) => value || 'RON',
      },
      {
        title: 'PDF',
        dataIndex: 'lastAttachPaymentNotificationPdf',
        key: 'lastAttachPaymentNotificationPdf',
        render: (value: boolean) => (value ? <Tag color='blue'>Da</Tag> : <Tag>-</Tag>),
      },
      {
        title: 'Ultimul To',
        dataIndex: 'lastTo',
        key: 'lastTo',
        render: (value: string) => value || '-',
      },
      {
        title: 'Ultimul Bcc',
        dataIndex: 'lastCc',
        key: 'lastCc',
        render: (value: string) => value || '-',
      },
      {
        title: 'Ultima trimitere',
        dataIndex: 'lastSentAt',
        key: 'lastSentAt',
        render: (value: string) => (value ? new Date(value).toLocaleString('ro-RO') : '-'),
      },
    ],
    [toNumber],
  );

  const historyInvoiceColumns = useMemo(
    () => [
      {
        title: 'Numar',
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
      },
      {
        title: 'Data',
        dataIndex: 'invoiceDate',
        key: 'invoiceDate',
        render: (value: string) => (value ? new Date(value).toLocaleDateString('ro-RO') : '-'),
      },
      {
        title: 'Scadenta',
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: (value: string) => (value ? new Date(value).toLocaleDateString('ro-RO') : '-'),
      },
      {
        title: 'Tip notificare',
        dataIndex: 'notificationType',
        key: 'notificationType',
        render: (_: unknown, record: NotificationInvoiceItem) => {
          if (!record.notificationType) {
            return '-';
          }

          return record.notificationType === 'Urmeaza' ? (
            <Tag color='blue'>Urmeaza</Tag>
          ) : (
            <Tag color='orange'>Scadenta/Restanta</Tag>
          );
        },
      },
      {
        title: 'Valoare',
        dataIndex: 'amount',
        key: 'amount',
        render: (value: number) => (value != null ? toNumber(value).toLocaleString('ro-RO') : '-'),
      },
      {
        title: 'Restant',
        dataIndex: 'outstanding',
        key: 'outstanding',
        render: (value: number) => (value != null ? toNumber(value).toLocaleString('ro-RO') : '-'),
      },
      {
        title: 'Moneda',
        dataIndex: 'currency',
        key: 'currency',
        render: (value: string) => value || 'RON',
      },
      {
        title: 'Trimis la',
        dataIndex: 'sentAt',
        key: 'sentAt',
        render: (value: string) => (value ? new Date(value).toLocaleString('ro-RO') : '-'),
      },
      {
        title: 'Status',
        dataIndex: 'result',
        key: 'result',
        render: (value: string) =>
          value ? <Tag color={value === 'Sent' ? 'green' : 'red'}>{value === 'Sent' ? 'Trimis' : 'Esuat'}</Tag> : '-',
      },
    ],
    [toNumber],
  );

  const handleRangeChange: RangePickerProps['onChange'] = (values) => {
    if (!values || values.length !== 2) {
      onDateRangeChange([null, null]);
      return;
    }

    const start = values[0] ? values[0].format('YYYY-MM-DD') : null;
    const end = values[1] ? values[1].format('YYYY-MM-DD') : null;
    onDateRangeChange([start, end]);
  };

  return (
    <div className='table-page-no-h-scroll'>
      <Card
        size='small'
        title='Validare primara - categorii active'
        style={{ marginBottom: 12 }}
        extra={isUpdatingCategory ? <Tag color='processing'>Se salveaza...</Tag> : null}
      >
        <Space wrap>
          {categorySettings.map((category) => (
            <Space key={category.category} style={{ padding: '4px 8px', border: '1px solid #f0f0f0', borderRadius: 8 }}>
              <span>{category.category}</span>
              <Switch
                size='small'
                checked={category.isEnabled}
                onChange={(checked) => onCategoryToggle(category.category, checked)}
                loading={isUpdatingCategory}
              />
            </Space>
          ))}
          {categorySettings.length === 0 ? <Tag>Nicio categorie configurata</Tag> : null}
        </Space>
      </Card>

      <Space style={{ marginBottom: 16 }} wrap>
        <DatePicker.RangePicker value={rangePickerValue} onChange={handleRangeChange} />
        <Input
          placeholder='Client'
          value={clientName}
          onChange={(e) => onClientNameChange(e.target.value)}
          style={{ width: 240 }}
        />
        <Input placeholder='CUI' value={cui} onChange={(e) => onCuiChange(e.target.value)} style={{ width: 200 }} />
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>
          Reincarca
        </Button>
      </Space>

      {isLoading ? (
        <Spin size='large' className='spinner' />
      ) : isError ? (
        <Alert message={errorMessage || 'Eroare la incarcare.'} type='error' />
      ) : (
        <div className='table-scroll-wrapper'>
          <Table
            rowKey={(record) => record.customerCui || record.clientName || ''}
            columns={historyColumns}
            dataSource={historyData}
            expandable={{
              expandedRowRender: (record: NotificationClientHistory) => (
                <Table<NotificationInvoiceItem>
                  rowKey='invoiceId'
                  columns={historyInvoiceColumns}
                  dataSource={record.invoices ?? []}
                  pagination={false}
                  size='middle'
                />
              ),
            }}
            scroll={{ x: true }}
            pagination={{
              current: historyPage,
              pageSize: historyPageSize,
              total: historyTotalItems,
              onChange: (nextPage, nextPageSize) => onHistoryPageChange(nextPage, nextPageSize || historyPageSize),
              showSizeChanger: true,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default NotificationTab;
